import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import {
  ArrowLeft,
  ArrowUpRight,
  BadgeCheck,
  BarChart3,
  CalendarCheck,
  CheckCircle2,
  Clock,
  CreditCard,
  Crown,
  Gauge,
  Lock,
  MessageSquareText,
  Shield,
  ShieldCheck,
  Sparkles,
  Tag,
  Target,
  TrendingUp,
  XCircle,
  Zap,
} from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { useWorkspace } from '../../../contexts/WorkspaceContext';

// ─── Stripe ───────────────────────────────────────────────────

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';

// Lazy-loaded: only resolves when first accessed (avoids crash when offline)
let _stripePromise: ReturnType<typeof loadStripe> | null = null;
function getStripePromise() {
  if (!stripePublishableKey) return null;
  if (!_stripePromise) {
    _stripePromise = loadStripe(stripePublishableKey).catch(() => null);
  }
  return _stripePromise;
}

const STRIPE_PRICE_IDS: Record<string, string> = {
  'starter_monthly': import.meta.env.VITE_STRIPE_PRICE_STARTER_MONTHLY || '',
  'starter_annual':  import.meta.env.VITE_STRIPE_PRICE_STARTER_ANNUAL  || '',
  'pro_monthly':     import.meta.env.VITE_STRIPE_PRICE_PRO_MONTHLY     || '',
  'pro_annual':      import.meta.env.VITE_STRIPE_PRICE_PRO_ANNUAL      || '',
};

// ─── Plan metadata ────────────────────────────────────────────

interface PlanMeta {
  name: string;
  icon: React.ElementType;
  price: { monthly: number; annual: number };
  features: string[];
  results: string[];
  capacity: string;
  color: string;
}

const PLAN_META: Record<string, PlanMeta> = {
  starter: {
    name: 'Starter',
    icon: BadgeCheck,
    price: { monthly: 497, annual: 4956 },
    color: 'var(--brand-primary)',
    capacity: '5 usuários · 10 projetos',
    features: [
      'Processos avançados',
      'Monitor de execuções',
      'Padrões e modelos',
      'Suporte por e-mail',
    ],
    results: [
      'Redução de 40% em retrabalho',
      'Visibilidade total das operações',
      'Padronização de processos',
      'Cultura de melhoria contínua',
    ],
  },
  pro: {
    name: 'Pro',
    icon: Sparkles,
    price: { monthly: 1490, annual: 14904 },
    color: 'var(--brand-primary)',
    capacity: 'Usuários e projetos ilimitados',
    features: [
      'Todas as funcionalidades',
      'Relatórios avançados',
      'API e Webhooks',
      'Modelos e governança',
      'Gamificação',
      'SLA dedicado',
      'Suporte prioritário',
    ],
    results: [
      'Redução de 60% em lead time',
      'Ganhos mensuráveis em eficiência',
      'Governança operacional completa',
      'ROI comprovado em 90 dias',
    ],
  },
};

// ─── Helpers ──────────────────────────────────────────────────

type BillingCycle = 'monthly' | 'annual';

function formatBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value);
}

function calcSavings(meta: PlanMeta) {
  const yearlyTotal = meta.price.annual;
  const monthlyEquiv = Math.round(yearlyTotal / 12);
  const fullPrice = meta.price.monthly * 12;
  const saved = fullPrice - yearlyTotal;
  const pct = Math.round((saved / fullPrice) * 100);
  return { yearlyTotal, monthlyEquiv, saved, pct };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN PAGE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { workspace } = useWorkspace();

  const urlPlan = searchParams.get('plan') || '';
  const urlCycle = (searchParams.get('cycle') || 'annual') as BillingCycle;
  const status = searchParams.get('status');

  // ── Active plan + cycle state (allows upgrade switching) ──
  const [plan, setPlan] = useState(urlPlan);
  const [cycle, setCycle] = useState<BillingCycle>(urlCycle);

  const meta = PLAN_META[plan];
  const priceKey = `${plan}_${cycle}`;
  const priceId = STRIPE_PRICE_IDS[priceKey];

  if (status === 'complete') {
    return <ActivationSuccess onGoBack={() => navigate('/administracao/billing')} planName={meta?.name} />;
  }

  const stripePromise = getStripePromise();

  if (!plan || !priceId || !stripePromise || !meta) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8"
           style={{ background: 'var(--bg-app)' }}>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Plano inválido ou configuração incompleta.
        </p>
        <button onClick={() => navigate('/administracao/billing')}
          className="enterprise-button-secondary px-4 py-2 rounded-md text-sm">
          <ArrowLeft size={14} className="inline mr-1" />Voltar
        </button>
      </div>
    );
  }

  const handleUpgradeToPro = () => setPlan('pro');

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ background: 'var(--bg-app)' }}>
      {/* ── Top bar ── */}
      <div className="flex items-center gap-3 px-5 py-2 border-b shrink-0"
        style={{ borderColor: 'var(--border-default)', background: 'var(--bg-elevated)' }}>
        <button onClick={() => navigate('/administracao/billing')}
          className="flex items-center gap-1.5 text-xs hover:underline transition-colors"
          style={{ color: 'var(--text-tertiary)' }}>
          <ArrowLeft size={14} />Voltar para Planos
        </button>
        <div className="flex-1" />
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
            <Shield size={11} />SSL 256-bit
          </span>
          <span className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
            <Lock size={11} />PCI DSS
          </span>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="flex-1 flex min-h-0 overflow-y-auto">
        <div className="w-full flex flex-col lg:flex-row gap-0 lg:gap-6 px-6 py-5">

          {/* ══════ LEFT ══════ */}
          <div className="w-full lg:flex-1 min-w-0 mb-4 lg:mb-0 flex flex-col gap-3">
            {/* Upgrade suggestion (only for Starter) */}
            {plan === 'starter' && (
              <UpgradeSuggestion cycle={cycle} onUpgrade={handleUpgradeToPro} />
            )}
            <LicenseActivationPanel meta={meta} cycle={cycle} onCycleChange={setCycle} />
          </div>

          {/* ══════ RIGHT ══════ */}
          <div className="w-full lg:w-[420px] shrink-0 flex flex-col">
            <PaymentPanel key={`${plan}_${cycle}`}
              plan={plan} cycle={cycle} priceId={priceId}
              workspaceId={workspace?.organizationId || ''} meta={meta} />
          </div>

        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// UPGRADE SUGGESTION (Starter → Pro)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function UpgradeSuggestion({ cycle, onUpgrade }: { cycle: BillingCycle; onUpgrade: () => void }) {
  const proMeta = PLAN_META.pro;
  const price = proMeta.price[cycle];
  const isAnnual = cycle === 'annual';
  const savings = calcSavings(proMeta);

  return (
    <div className="rounded border p-3.5 flex items-center gap-3"
      style={{
        borderColor: 'var(--brand-primary)',
        background: 'var(--brand-primary-soft, rgba(59,130,246,0.06))',
      }}>
      <div className="w-9 h-9 rounded flex items-center justify-center shrink-0"
        style={{ background: 'var(--brand-primary)', opacity: 0.9 }}>
        <Crown size={18} color="#fff" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>
          Upgrade recomendado — DataFlow Pro
        </p>
        <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
          Usuários, processos e projetos ilimitados · {formatBRL(price)}/{isAnnual ? 'ano' : 'mês'}
          {isAnnual && ` (${formatBRL(savings.monthlyEquiv)}/mês)`}
        </p>
      </div>
      <button onClick={onUpgrade}
        className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
        style={{ background: 'var(--brand-primary)', color: '#fff' }}>
        Ativar Pro <ArrowUpRight size={12} />
      </button>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// BILLING TOGGLE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function BillingToggle({ cycle, onChange, savingsPct }: {
  cycle: BillingCycle; onChange: (c: BillingCycle) => void; savingsPct: number;
}) {
  return (
    <div className="rounded border p-3 flex items-center gap-3"
      style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
      <CalendarCheck size={15} style={{ color: 'var(--text-secondary)' }} />
      <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Cobrança</span>
      <div className="flex rounded-md overflow-hidden border ml-auto"
        style={{ borderColor: 'var(--border-default)' }}>
        <button
          onClick={() => onChange('monthly')}
          className="px-3.5 py-1.5 text-xs font-medium transition-all"
          style={{
            background: cycle === 'monthly' ? 'var(--brand-primary)' : 'transparent',
            color: cycle === 'monthly' ? '#fff' : 'var(--text-secondary)',
          }}
        >
          Mensal
        </button>
        <button
          onClick={() => onChange('annual')}
          className="px-3.5 py-1.5 text-xs font-medium transition-all flex items-center gap-1.5"
          style={{
            background: cycle === 'annual' ? 'var(--brand-primary)' : 'transparent',
            color: cycle === 'annual' ? '#fff' : 'var(--text-secondary)',
          }}
        >
          Anual
          <span className="rounded px-1.5 py-0.5 text-[10px] font-bold"
            style={{
              background: cycle === 'annual' ? 'rgba(255,255,255,0.25)' : 'rgba(245,158,11,0.12)',
              color: cycle === 'annual' ? '#fff' : '#d97706',
            }}>
            -{savingsPct}%
          </span>
        </button>
      </div>
      {cycle === 'annual' && (
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded"
          style={{ background: 'rgba(245,158,11,0.12)', color: '#d97706' }}>
          Mais econômico
        </span>
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// LEFT PANEL — License Activation
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function LicenseActivationPanel({ meta, cycle, onCycleChange }: {
  meta: PlanMeta; cycle: BillingCycle; onCycleChange: (c: BillingCycle) => void;
}) {
  const Icon = meta.icon;
  const price = meta.price[cycle];
  const isAnnual = cycle === 'annual';
  const savings = useMemo(() => calcSavings(meta), [meta]);

  return (
    <>
      {/* ── Billing Toggle ── */}
      <BillingToggle cycle={cycle} onChange={onCycleChange} savingsPct={savings.pct} />

      {/* ── License Header + Price ── */}
      <div className="rounded border p-4" style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded flex items-center justify-center shrink-0"
            style={{ background: 'var(--brand-primary-soft)' }}>
            <Crown size={22} style={{ color: 'var(--brand-primary)' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wider mb-0.5"
              style={{ color: 'var(--brand-primary)' }}>
              Ativação de Licença
            </p>
            <h2 className="text-xl font-bold leading-tight mb-0.5" style={{ color: 'var(--text-primary)' }}>
              DataFlow — {meta.name}
            </h2>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              {meta.capacity}
            </p>
          </div>
          <div className="text-right shrink-0">
            <span className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {formatBRL(price)}
            </span>
            <span className="text-xs block" style={{ color: 'var(--text-tertiary)' }}>
              /{isAnnual ? 'ano' : 'mês'}
            </span>
            {isAnnual && (
              <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                ({formatBRL(savings.monthlyEquiv)}/mês)
              </span>
            )}
          </div>
        </div>

        {/* Savings callout */}
        {isAnnual && (
          <div className="mt-3 rounded-md px-3 py-2 flex items-center gap-2"
            style={{ background: 'var(--risk-ok-soft, rgba(22,163,74,0.08))' }}>
            <Tag size={13} style={{ color: 'var(--risk-ok, #16a34a)' }} />
            <span className="text-xs font-medium" style={{ color: 'var(--risk-ok, #16a34a)' }}>
              Economia de {formatBRL(savings.saved)}/ano — equivale a 2 meses grátis
            </span>
          </div>
        )}
      </div>

      {/* ── Value proposition ── */}
      <div className="rounded border px-4 py-3"
        style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
        <div className="flex items-start gap-2.5">
          <MessageSquareText size={14} className="shrink-0 mt-0.5" style={{ color: 'var(--brand-primary)' }} />
          <p className="text-[12.5px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            Planeje e desenhe seus processos, identifique oportunidades de melhoria,
            gere automaticamente o processo futuro otimizado, acompanhe a execução das melhorias
            e transforme o processo em um fluxo operacional que garante excelência na execução.
            Tudo guiado de forma simples e estruturada dentro do DataFlow.
          </p>
        </div>
      </div>

      {/* ── Features + Results ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1">
        <div className="rounded border p-4 flex flex-col" style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-3">
            <Icon size={15} style={{ color: 'var(--brand-primary)' }} />
            <h4 className="text-[13px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-primary)' }}>
              Funcionalidades incluídas
            </h4>
          </div>
          <div className="space-y-3 flex-1">
            {meta.features.map((f) => (
              <div key={f} className="flex items-center gap-2.5">
                <CheckCircle2 size={14} className="shrink-0" style={{ color: 'var(--risk-ok, #16a34a)' }} />
                <span className="text-[13px]" style={{ color: 'var(--text-primary)', opacity: 0.8 }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded border p-4 flex flex-col" style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={15} style={{ color: 'var(--risk-ok, #16a34a)' }} />
            <h4 className="text-[13px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-primary)' }}>
              Resultados esperados
            </h4>
          </div>
          <div className="space-y-3 flex-1">
            {meta.results.map((r) => (
              <div key={r} className="flex items-center gap-2.5">
                <Target size={14} className="shrink-0" style={{ color: 'var(--brand-primary)' }} />
                <span className="text-[13px]" style={{ color: 'var(--text-primary)', opacity: 0.8 }}>{r}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-3 gap-3">
        <KpiCard icon={Gauge} label="Lead Time" value="-60%" />
        <KpiCard icon={BarChart3} label="Eficiência" value="+45%" />
        <KpiCard icon={Clock} label="ROI em" value="90 dias" />
      </div>

      {/* ── Trust bar ── */}
      <div className="rounded border px-4 py-2.5 flex items-center justify-between"
        style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
        {[
          { icon: Zap, text: 'Ativação imediata' },
          { icon: XCircle, text: 'Cancele quando quiser' },
          { icon: ShieldCheck, text: 'Garantia 7 dias' },
          { icon: Lock, text: 'Pagamento seguro' },
        ].map(({ icon: I, text }, idx) => (
          <React.Fragment key={text}>
            {idx > 0 && <div className="w-px h-3.5" style={{ background: 'var(--border-default)' }} />}
            <div className="flex items-center gap-1.5">
              <I size={12} style={{ color: 'var(--text-tertiary)' }} />
              <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{text}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </>
  );
}

// ─── Small components ─────────────────────────────────────────

function KpiCard({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="rounded border px-3 py-3 text-center transition-all duration-300 hover:border-[var(--brand-primary)] hover:shadow-[0_0_8px_rgba(59,130,246,0.12)]"
      style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
      <Icon size={18} className="mx-auto mb-1.5" style={{ color: 'var(--brand-primary)' }} />
      <p className="text-lg font-bold leading-tight" style={{ color: 'var(--text-primary)' }}>{value}</p>
      <p className="text-[10px] mt-0.5 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{label}</p>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// RIGHT PANEL — Payment
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function PaymentPanel({ plan, cycle, priceId, workspaceId, meta }: {
  plan: string; cycle: string; priceId: string; workspaceId: string; meta: PlanMeta;
}) {
  return (
    <div className="flex flex-col gap-2.5 h-full">
      {/* Payment header */}
      <div className="rounded border px-4 py-4 flex items-center gap-2.5 shrink-0"
        style={{
          borderColor: 'var(--border-default)',
          background: 'var(--bg-surface)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
        <CreditCard size={16} style={{ color: 'var(--text-secondary)' }} />
        <div className="flex-1">
          <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
            Pagamento seguro
          </p>
          <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
            Criptografia ponta a ponta · SSL 256-bit · PCI DSS · Stripe
          </p>
        </div>
        <Lock size={12} style={{ color: 'var(--text-tertiary)' }} />
      </div>

      {/* Stripe form */}
      <div className="rounded border overflow-hidden"
        style={{
          borderColor: 'var(--border-default)',
          background: 'var(--bg-surface)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
        <div className="stripe-checkout-compact">
          <EmbeddedCheckoutWrapper plan={plan} cycle={cycle} priceId={priceId} workspaceId={workspaceId} />
        </div>
      </div>

      {/* Footer */}
      <p className="text-[10px] text-center leading-relaxed shrink-0 pt-1" style={{ color: 'var(--text-tertiary)', opacity: 0.7 }}>
        Ao ativar sua licença, você concorda com os Termos de Serviço.
        Processado por Stripe Inc.
      </p>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SUCCESS STATE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function ActivationSuccess({ onGoBack, planName }: { onGoBack: () => void; planName?: string }) {
  return (
    <div className="flex-1 flex items-center justify-center" style={{ background: 'var(--bg-app)' }}>
      <div className="flex flex-col items-center gap-6 max-w-lg text-center px-6">
        <div className="w-20 h-20 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(var(--enterprise-success-rgb, 50 213 131), 0.12)' }}>
          <CheckCircle2 size={36} style={{ color: 'var(--risk-ok, #16a34a)' }} />
        </div>
        <div>
          <p className="text-[11px] font-medium uppercase tracking-wider mb-2"
            style={{ color: 'var(--risk-ok, #16a34a)' }}>
            Licença ativada
          </p>
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            {planName ? `Plano ${planName} ativado com sucesso` : 'Licença ativada com sucesso'}
          </h2>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            Todas as funcionalidades do seu plano já estão disponíveis.
            Sua organização pode começar a utilizar os recursos imediatamente.
          </p>
        </div>
        <button onClick={onGoBack}
          className="enterprise-button-primary px-6 py-2.5 rounded-md text-sm font-medium">
          Ir para Plano e Assinatura
        </button>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// EMBEDDED CHECKOUT WRAPPER
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function EmbeddedCheckoutWrapper({ plan, cycle, priceId, workspaceId }: {
  plan: string; cycle: string; priceId: string; workspaceId: string;
}) {
  const [error, setError] = useState<string | null>(null);

  const fetchClientSecret = useCallback(async () => {
    const returnUrl = `${window.location.origin}/checkout?status=complete`;

    const { data, error: fnError } = await supabase.functions.invoke('create-checkout', {
      body: {
        workspace_id: workspaceId,
        price_id: priceId,
        plan_slug: plan,
        billing_cycle: cycle,
        ui_mode: 'embedded',
        return_url: returnUrl,
      },
    });

    if (fnError) {
      setError(fnError.message || 'Erro ao criar sessão de checkout.');
      throw new Error(fnError.message);
    }

    if (!data?.clientSecret) {
      const msg = data?.error || 'Nenhum client secret retornado.';
      setError(msg);
      throw new Error(msg);
    }

    return data.clientSecret as string;
  }, [workspaceId, priceId, plan, cycle]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3 px-6">
        <Shield size={24} style={{ color: 'var(--text-tertiary)' }} />
        <p className="text-sm text-center" style={{ color: 'var(--status-error, #ef4444)' }}>{error}</p>
      </div>
    );
  }

  const stripePromiseLocal = getStripePromise();
  if (!stripePromiseLocal) return null;

  return (
    <EmbeddedCheckoutProvider
      stripe={stripePromiseLocal}
      options={{ fetchClientSecret, onComplete: () => {} }}
    >
      <EmbeddedCheckout />
    </EmbeddedCheckoutProvider>
  );
}
